import { Component, ElementRef, EventEmitter, inject, input, OnDestroy, OnInit, DestroyRef, ChangeDetectionStrategy, effect, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Comment, IUser, keyActions, mentionsRegex } from '@miq/shared-models';
import { UserInfoResolver, AdminService, TeamService } from '@miq/platform';
import { combineLatest, EMPTY, merge, of, Subject, timer } from 'rxjs';
import { debounce, debounceTime, distinctUntilChanged, filter, map, switchMap, catchError } from 'rxjs/operators';
import { CommentsApiService } from './comments-api.service';
import { NgClass, TitleCasePipe } from '@angular/common';
import { EntryCommentComponent } from './entry-comment/entry-comment.component';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { L10nTranslationModule } from 'angular-l10n';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
/**
 * L2-S1/T7: moved from `src/app/shared/components/comments/comments.component.ts`, where it was a
 * sanctioned reports-remote seam (the remote's `report-details` renders it for report discussions).
 *
 * ONE blocker had to be removed: `sendEmailToMentionedUsers` used to read the record being viewed
 * with `store.selectSnapshot(CurrentRecordState.getState)`, which dragged ~188 files of the host's
 * NGXS current-record action tree into every bundle that touched this component, to read exactly
 * two scalars — `type` and `id`. They are now optional INPUTS (`recordType` / `recordId`).
 *
 * That is behaviour-preserving, not a shortcut: `sendEmailToMentionedUsers` is only ever reached
 * behind `autoSuggestUsers()` (see `onMessageSent` and `saveEditComment`), and the ONLY call site
 * in the workspace that sets `autoSuggestUsers` is the record-details `visual-widget`, which is
 * exactly where `CurrentRecordState` is populated — and which now binds the two new inputs from the
 * same selector. The other three call sites (reports remote, service-ticket detail, and the
 * comments drawer) leave `autoSuggestUsers` false, so they never took the snapshot at all.
 *
 * The old host path is a re-export shim.
 */
const EVERY_30_SECONDS = 30000;
const MENTION_SEARCH_DEBOUNCE_MS = 200;
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-comments',
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss',
  imports: [
    NgClass,
    L10nTranslationModule,
    EntryCommentComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatButton,
    MatIcon,
    MatProgressSpinnerModule,
    TitleCasePipe
],
})
export class CommentsComponent implements OnInit, OnDestroy {
  protected destroyRef = inject(DestroyRef);
  title = input<string>();
  type = input<string>();
  readonly conversationId = input<string>('');
  /**
   * The record a mention notification should point at. Optional: only the record-details call site
   * (which sets `autoSuggestUsers`) supplies them; everywhere else no notification is ever sent.
   * Replaces the former `CurrentRecordState` snapshot — see the class comment.
   */
  readonly recordType = input<string>();
  readonly recordId = input<number>();
  private readonly submitRequested = new EventEmitter<string>();
  comments = signal<Comment[]>([]);
  currentUserEmail: any;
  messageGroup: FormGroup<{ message: FormControl<string | null> }>;
  loading = signal(false);
  private readonly refetch$ = new Subject<void>();
  private readonly commentsContainer = viewChild<ElementRef<HTMLDivElement>>('commentsContainer');
  private readonly commentCount = signal(0);
  private readonly scrollTrigger = signal(0);
  // Signal (not a plain array): with OnPush, a fresh result arriving while the
  // dropdown is already open changes neither showMentionDropdown (true→true) nor
  // selectedMentionIndex (0→0), so nothing else would trigger a repaint.
  mentionUsers = signal<IUser[]>([]);
  mentionSearchLoading = signal(false);
  private readonly mentionSearchTerm$ = new Subject<string>();
  showMentionDropdown = signal(false);
  selectedMentionIndex = signal(0);
  currentMentionSearch = '';
  mentionStartPos = 0;
  dropdownPosition = { top: 0, left: 0 };
  autoSuggestUsers = input(false);
  mentionTextToEmail: Map<string, string> = new Map();
  private lastSentMessage = '';
  // ── Edit state ────────────────────────────────────────────────────────────
  editingComment = signal<Comment | null>(null);
  editText = '';
  private readonly mine = comment => ({ ...comment, itsMe: this.currentUserEmail === comment.email });
  private readonly myComments = comments => comments.map(this.mine);
  private readonly userResolver = inject(UserInfoResolver);
  private readonly commentsApi = inject(CommentsApiService);
  private readonly fb = inject(FormBuilder);
  private readonly adminService = inject(AdminService);
  public teamService = inject(TeamService);
  private readonly logger = inject<CbreLogger>(CbreLoggerToken);
  constructor() {
    const { emailAddress } = this.userResolver.resolve();
    this.currentUserEmail = emailAddress;
    this.messageGroup = new FormGroup({ message: new FormControl<string | null>(null) });
    this.loading.set(true);
    this.submitRequested
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        switchMap(message => this.commentsApi.create(this.type(), this.conversationId(), { message }))
      )
      .subscribe({ next: this.onMessageSent.bind(this), error: this.onMessageSentFailure.bind(this) });
    // Mention user search: debounced per keystroke, and switchMap cancels the
    // previous in-flight request so responses can never land out of order.
    // '' is the cancel signal (pushed by cancelMentionSearch on close/select) —
    // it skips the debounce (of(0) emits synchronously) and unsubscribes the
    // in-flight search immediately, so a late response can never repaint or
    // reopen the dropdown after the user has already selected or moved on.
    this.mentionSearchTerm$
      .pipe(
        debounce(term => (term ? timer(MENTION_SEARCH_DEBOUNCE_MS) : of(0))),
        switchMap(term => term
          ? this.adminService.userSearch(term.trim()).pipe(
              // The API returns a null body when nothing matches — normalize it,
              // and wrap the response so a real empty result stays distinguishable
              // from the null cancel sentinel below.
              map(users => ({ users: users ?? [] })),
              catchError(error => {
                this.logger.error(`Error searching users for mentions: ${error}`);
                return of({ users: [] as IUser[] });
              })
            )
          : of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        this.mentionSearchLoading.set(false);
        if (result === null) return; // cancelled — the close paths already updated the UI
        this.mentionUsers.set(result.users);
        // Keep the dropdown open on an empty result — it shows "no matching records found".
        this.showMentionDropdown.set(true);
        this.selectedMentionIndex.set(0);
      });
    // Single fetch pipeline: fires on (type, conversationId) change (deduped),
    // on explicit refetch$ (after posting), and every 30s. switchMap cancels
    // any inflight request so we never stack calls.
    const inputs$ = combineLatest([
      toObservable(this.type),
      toObservable(this.conversationId),
    ]).pipe(
      filter(([t, id]) => !!t && !!id),
      distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
    );
    const poll$ = inputs$.pipe(switchMap(() => timer(EVERY_30_SECONDS, EVERY_30_SECONDS)));
    merge(inputs$, poll$, this.refetch$)
      .pipe(
        filter(() => !!this.type() && !!this.conversationId()),
        // CM-34032: catchError lives INSIDE the switchMap projection, not after it.
        // This poll had no error handling at all, so a 504 on GET /notes/{type}/{id}
        // didn't just throw an unhandled exception — merge()'s single subscription
        // died with it, silently killing comment auto-refresh for the rest of this
        // component's lifetime (until the panel is closed and reopened). Catching
        // inside the projected observable lets switchMap recover and keep listening
        // for the next poll/refetch/input-change tick instead.
        switchMap(() =>
          this.commentsApi.getConverstaion(this.type(), this.conversationId()).pipe(
            catchError(error => {
              this.logger.error(`[comments] poll failed for ${this.type()}/${this.conversationId()}: ${error}`);
              return EMPTY;
            })
          )
        ),
        map(this.myComments),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(this.onFetchComments.bind(this));
    // Scroll to bottom whenever scrollTrigger ticks (after initial load or
    // when new comments arrive). rAF lets the DOM paint the new entries
    // before measuring scrollHeight.
    effect(() => {
      if (this.scrollTrigger() > 0) {
        requestAnimationFrame(() => this.scrollToBottom());
      }
    });
    // Close the mention dropdown whenever any scroll occurs —
    // the position:fixed dropdown cannot follow a scrolling input.
    // Covers both the internal .comments scroll and outer page scroll.
    // Scrolling inside the dropdown itself is excluded so users can browse the list.
    effect((onCleanup) => {
      const handler = (event: Event) => {
        if (this.showMentionDropdown()) {
          const dropdownEl = document.querySelector('.mention-dropdown');
          if (dropdownEl && dropdownEl.contains(event.target as Node)) {
            return;
          }
          this.closeMentionDropdown();
        }
      };
      window.addEventListener('scroll', handler, { passive: true, capture: true });
      onCleanup(() => window.removeEventListener('scroll', handler, { capture: true }));
    });
  }
  ngOnInit() {
    /* required for static lable translation */
  }
  ngOnDestroy() {
    /** Required by untilDestroy */
  }
  fetchComments() {
    this.refetch$.next();
  }
  onFetchComments(comments) {
    const newCount = comments?.length || 0;
    const previousCount = this.commentCount();
    this.commentCount.set(newCount);
    this.comments.set(comments || []);
    this.loading.set(false);
    if (newCount > 0 && (previousCount === 0 || newCount > previousCount)) {
      this.scrollTrigger.update(v => v + 1);
    }
  }
  private scrollToBottom(): void {
    const element = this.commentsContainer()?.nativeElement;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }
  sendMessage($event) {
    $event.preventDefault();
    $event.stopPropagation();
    const { message } = this.messageGroup.value;
    const trimmed = message?.trim();
    if (trimmed) {
      this.lastSentMessage = trimmed;
      this.submitRequested.emit(trimmed);
    }
  }
  get messageControl() {
    return this.messageGroup.controls.message;
  }
  onMessageSent() {
    this.messageControl.setValue('');
    if(this.autoSuggestUsers() && this.mentionTextToEmail?.size > 0) {
      const emailUsers = Array.from(this.mentionTextToEmail.values());
      this.sendEmailToMentionedUsers(emailUsers, this.lastSentMessage);
      this.mentionTextToEmail.clear();
    }
    this.lastSentMessage = '';
    this.fetchComments();
  }
  onMessageSentFailure(error) {
    this.logger.error(`${error}`);
  }

  // ── Edit comment — all logic stays in CommentsComponent ──────────────────

  startEditComment(comment: Comment): void {
    this.editText = comment.message;
    this.editingComment.set(comment);
    // Clear any in-progress new-comment mention state (incl. in-flight search)
    this.mentionTextToEmail.clear();
    this.closeMentionDropdown();
    if (this.autoSuggestUsers()) {
      this.preloadMentionsFromText(comment.message);
    }
  }

  cancelEditComment(): void {
    this.editingComment.set(null);
    this.editText = '';
    this.mentionTextToEmail.clear();
    this.closeMentionDropdown();
  }

  saveEditComment(): void {
    const comment = this.editingComment();
    if (!comment) return;
    const newMessage = this.editText.trim();
    if (!newMessage || newMessage === comment.message) {
      this.cancelEditComment();
      return;
    }
    // The edit textarea is about to go away — drop any in-flight mention
    // search so its response can't reopen the dropdown over a closed editor.
    this.closeMentionDropdown();
    this.commentsApi.update(this.type(), this.conversationId(), { id: comment.id, message: newMessage }).subscribe({
      next: () => {
        this.comments.update(list =>
          list.map(c => c.id === comment.id ? { ...c, message: newMessage, updatedTs: new Date() } : c)
        );
        // Read the map only now: preloadMentionsFromText populates it via async
        // userSearch calls, so a snapshot taken before the update round-trip can
        // race and see it empty. (Same pattern as the comments drawer.)
        // cancelEditComment() below clears the map, so read before calling it.
        if (this.autoSuggestUsers() && this.mentionTextToEmail.size > 0) {
          this.sendEmailToMentionedUsers(Array.from(this.mentionTextToEmail.values()), newMessage);
        }
        this.cancelEditComment();
      },
      error: err => this.logger.error(`Failed to update comment: ${err}`),
    });
  }

  // A mention is inserted as "@FirstName LastName", but in an existing comment
  // free text can directly follow it ("…@John Smith please review…"), so the
  // name's end cannot be guessed from the text alone. Instead: search users by
  // the first word(s) after "@", then register only those whose exact
  // "@FirstName LastName" display text actually occurs in the comment.
  private preloadMentionsFromText(text: string): void {
    const tokenRegex = /@([A-Za-zÀ-ſ][A-Za-zÀ-ſ'’-]*)(?:\s+([A-Za-zÀ-ſ][A-Za-zÀ-ſ'’-]*))?/g;
    let match: RegExpExecArray | null;
    while ((match = tokenRegex.exec(text)) !== null) {
      const [, first, second] = match;
      const candidates = [second ? `${first} ${second}` : '', first].filter(term => term.length >= 2);
      if (candidates.length) {
        this.searchAndRegisterMentions(candidates, text);
      }
    }
  }

  // Try each search term in order; fall back to the next only when the current
  // one registered nothing (e.g. "John and" → no user → retry with "John").
  private searchAndRegisterMentions(candidates: string[], text: string): void {
    const [term, ...fallbacks] = candidates;
    this.adminService.userSearch(term.trim()).pipe(
      catchError(() => of([])),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(users => {
      if (!this.registerMentionsFoundInText(users, text) && fallbacks.length) {
        this.searchAndRegisterMentions(fallbacks, text);
      }
    });
  }

  // Register every returned user whose exact "@First Last" occurs in the text,
  // requiring a non-letter boundary after it so "@John Smith" is not matched
  // inside "@John Smithson". Keys must equal the literal substring in the text
  // (case-sensitive) so syncMentionedEmails keeps them alive while typing.
  private registerMentionsFoundInText(users: IUser[], text: string): boolean {
    let registered = false;
    (users || []).forEach(user => {
      if (!user?.firstName || !user?.lastName) return;
      const mentionText = `@${user.firstName} ${user.lastName}`;
      if (this.mentionTextToEmail.has(mentionText)) {
        registered = true;
        return;
      }
      const escaped = mentionText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (new RegExp(`${escaped}(?![A-Za-zÀ-ſ])`).test(text)) {
        this.mentionTextToEmail.set(mentionText, user.emailAddress);
        registered = true;
      }
    });
    return registered;
  }
  onTextInput(event): void {
    const input = event.target;
    const text = input.value;
    const cursorPos = input.selectionStart;
    
    if (!this.showMentionDropdown()) {
      this.syncMentionedEmails(text);
    }
    
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      if (charBeforeAt === ' ' || lastAtIndex === 0) {
        const searchText = textBeforeCursor.substring(lastAtIndex + 1);
        
        if (!searchText.includes(' ') && searchText.length <= 30) {
          this.currentMentionSearch = searchText;
          this.mentionStartPos = lastAtIndex;
          
          if (searchText.length >= 2) {
            this.searchUsersForMention(searchText);
            this.positionDropdown(input);
          } else if (searchText.length === 0) {
            this.closeMentionDropdown();
          }
          return;
        }
      }
    }
    this.closeMentionDropdown();
  }
  onKeyDown(event: KeyboardEvent): void {
    if (this.showMentionDropdown()) {
      // Escape closes the dropdown even in the empty "no matching records" state.
      if (event.key === keyActions.escape) {
        event.preventDefault();
        this.closeMentionDropdown();
        return;
      }
      if (this.mentionUsers().length > 0) {
        switch (event.key) {
          case keyActions.arrowDown:
            event.preventDefault();
            this.selectedMentionIndex.set(Math.min(this.selectedMentionIndex() + 1, this.mentionUsers().length - 1));
            return;
          case keyActions.arrowUp:
            event.preventDefault();
            this.selectedMentionIndex.set(Math.max(this.selectedMentionIndex() - 1, 0));
            return;
          case keyActions.enter:
          case keyActions.tab:
            if (this.mentionUsers()[this.selectedMentionIndex()]) {
              event.preventDefault();
              this.selectMention(this.mentionUsers()[this.selectedMentionIndex()]);
            }
            return;
        }
      }
    }
    // Enter (no shift, no dropdown) while editing a comment → save
    if (event.key === 'Enter' && !event.shiftKey && this.editingComment()) {
      event.preventDefault();
      this.saveEditComment();
    }
  }
  closeMentionDropdown(): void {
    this.cancelMentionSearch();
    this.showMentionDropdown.set(false);
    this.selectedMentionIndex.set(0);
  }
  private searchUsersForMention(searchTerm: string): void {
    this.mentionSearchLoading.set(true);
    this.mentionSearchTerm$.next(searchTerm);
  }
  // Cancels the pending/in-flight user search so its response is ignored.
  private cancelMentionSearch(): void {
    this.mentionSearchLoading.set(false);
    this.mentionSearchTerm$.next('');
  }
  private positionDropdown(input: HTMLInputElement | HTMLTextAreaElement): void {
    const rect = input.getBoundingClientRect();
    // Viewport-relative coordinates for position:fixed dropdown —
    // unaffected by ancestor overflow:hidden or transforms.
    this.dropdownPosition = {
      top: rect.bottom + 5,
      left: rect.left
    };
  }
  selectMention(user: IUser): void {
    const isEditing = this.editingComment() !== null;
    const currentValue = isEditing ? (this.editText || '') : (this.messageControl.value || '');
    const beforeMention = currentValue.substring(0, this.mentionStartPos);
    const afterMention = currentValue.substring(this.mentionStartPos + this.currentMentionSearch.length + 1);

    const mentionText = `@${user.firstName} ${user.lastName}`;
    const newValue = beforeMention + mentionText + ' ' + afterMention;

    this.mentionTextToEmail.set(mentionText, user.emailAddress);

    if (isEditing) {
      this.editText = newValue;
    } else {
      this.messageControl.setValue(newValue);
    }

    // Also cancels any in-flight search so its late response
    // cannot reopen the dropdown after this selection.
    this.closeMentionDropdown();

    setTimeout(() => {
      this.syncMentionedEmails(newValue);
      const selector = isEditing
        ? 'textarea.edit-textarea'
        : 'input[formControlName="message"]';
      const input = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement;
      if (input) {
        input.focus();
        const newCursorPos = beforeMention.length + mentionText.length + 1;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }
  private extractMentionsFromText(text: string): string[] {
    const mentions: string[] = [];
    
    this.mentionTextToEmail.forEach((email, mentionText) => {
      if (text.includes(mentionText)) {
        mentions.push(mentionText);
      }
    });
    
    if (mentions.length > 0) {
      return mentions;
    }
    
    const mentionRegex = mentionsRegex;
    let match;
    
    mentionRegex.lastIndex = 0;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(`@${match[1]}`);
    }
    
    return mentions;
  }
  private syncMentionedEmails(text: string): void {
    const currentMentions = this.extractMentionsFromText(text);
    const mentionsToRemove: string[] = [];
    
    this.mentionTextToEmail.forEach((email, mentionText) => {
      if (!currentMentions.includes(mentionText)) {
        mentionsToRemove.push(mentionText);
      }
    });
    
    mentionsToRemove.forEach(mentionText => {
      this.mentionTextToEmail.delete(mentionText);
    });
  }
  sendEmailToMentionedUsers(emailUsers: string[], message: string = ''): void {
    if (!emailUsers?.length) return;

    const payload = {
      recipients: emailUsers,
      entity: this.recordType(),
      eventName: "ENTITY_COMMENT_MENTION_NOTICE",
      recordId: this.recordId(),
      data: {
        USER_MAIL: this.currentUserEmail,
        MENTION_MESSAGE: message
      }
    };

    this.teamService.sendEmailNotification(payload).subscribe();
  }
}
